import { LegalLayout } from "@/components/LegalLayout";

const H2 = ({ children }) => (
  <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white mt-4">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="text-base text-slate-400 leading-relaxed">{children}</p>
);

const UL = ({ items }) => (
  <ul className="flex flex-col gap-2 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-base text-slate-400 leading-relaxed">
        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#00DC82]" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Section = ({ children }) => (
  <section className="flex flex-col gap-4">{children}</section>
);

const Divider = () => <div className="h-px w-full bg-white/10" />;

export default function Terms() {
  return (
    <LegalLayout title="Terms of Use">
      <Section>
        <P>
          <strong className="font-semibold text-white">Last Updated:</strong> July 2026
        </P>
      </Section>

      <Section>
        <H2>Terms of Use</H2>
        <P>Welcome to MockTP.</P>
        <P>
          These Terms of Use govern your use of the MockTP website and MockTP
          applications.
        </P>
        <P>
          By accessing the website, downloading the app or using any MockTP services, you
          agree to these Terms.
        </P>
        <P>If you do not agree with these Terms, please do not use MockTP.</P>
      </Section>

      <Divider />

      <Section>
        <H2>About MockTP</H2>
        <P>
          MockTP is developed by{" "}
          <strong className="font-semibold text-white">MockTP Labs</strong>.
        </P>
        <P>
          MockTP is designed to help professional driving instructors record mock driving
          assessments, monitor student progress and produce professional assessment
          reports.
        </P>
        <P>
          MockTP is intended for use by professional driving instructors aged 18 years or
          older.
        </P>
      </Section>

      <Divider />

      <Section>
        <H2>Website Use</H2>
        <P>You agree to use the MockTP website only for lawful purposes.</P>
        <P>You must not:</P>
        <UL
          items={[
            "Attempt to interfere with the operation or security of the website.",
            "Attempt to gain unauthorised access to any part of the website or its systems.",
            "Upload malicious software or harmful content.",
            "Use the website in a way that could damage or disrupt the service.",
          ]}
        />
      </Section>

      <Divider />

      <Section>
        <H2>App Use</H2>
        <P>MockTP is provided as a professional tool to assist driving instructors.</P>
        <P>The instructor remains fully responsible for:</P>
        <UL
          items={[
            "Conducting driving lessons safely.",
            "Supervising students.",
            "Making professional judgement during assessments.",
            "Complying with all applicable laws and driving regulations.",
          ]}
        />
        <P>
          MockTP is an assessment and record-keeping tool. It does not replace
          professional instruction or legal responsibilities.
        </P>
      </Section>

      <Divider />

      <Section>
        <H2>Intellectual Property</H2>
        <P>
          The MockTP website, applications, branding, logos, text, graphics and software
          are the property of{" "}
          <strong className="font-semibold text-white">MockTP Labs</strong> unless
          otherwise stated.
        </P>
        <P>
          You may not copy, modify, distribute, reverse engineer or reproduce any part of
          MockTP without prior written permission.
        </P>
      </Section>

      <Divider />

      <Section>
        <H2>Service Availability</H2>
        <P>MockTP is being prepared for public launch.</P>
        <P>Public store availability may change as app-store review and distribution progress.</P>
        <P>Features may change, be added or removed before public release.</P>
        <P>Your feedback helps improve MockTP before wider availability.</P>
      </Section>

      <Divider />

      <Section>
        <H2>Limitation of Liability</H2>
        <P>MockTP is provided on an "as available" basis.</P>
        <P>
          While reasonable efforts are made to ensure the accuracy and reliability of the
          website and app, no guarantee is made that the service will always be
          uninterrupted or error-free.
        </P>
        <P>
          To the fullest extent permitted by Irish law, MockTP Labs shall not be liable for
          any indirect, incidental or consequential loss arising from the use of the website
          or app.
        </P>
      </Section>

      <Divider />

      <Section>
        <H2>Changes to These Terms</H2>
        <P>These Terms of Use may be updated from time to time.</P>
        <P>The latest version will always be available on the MockTP website.</P>
        <P>
          Continued use of MockTP after changes take effect constitutes acceptance of the
          updated Terms.
        </P>
      </Section>

      <Divider />

      <Section>
        <H2>Governing Law</H2>
        <P>These Terms of Use are governed by the laws of Ireland.</P>
        <P>
          Any disputes arising from the use of MockTP shall be subject to the jurisdiction
          of the Irish courts.
        </P>
      </Section>

      <Divider />

      <Section>
        <H2>Contact</H2>
        <P>If you have questions about these Terms of Use, you can contact us at:</P>
        <P>
          <a href="mailto:legal@mocktp.app" className="text-[#00DC82] hover:underline">
            <strong className="font-semibold">legal@mocktp.app</strong>
          </a>
        </P>
        <P>
          Website:{" "}
          <a
            href="https://mocktp.app"
            className="text-[#00DC82] hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            <strong className="font-semibold">mocktp.app</strong>
          </a>
        </P>
      </Section>

      <Divider />

      <Section>
        <H2>Final Statement</H2>
        <P>Thank you for using MockTP.</P>
        <P>
          Our goal is simple: to provide professional driving instructors with practical
          tools that make mock driving assessments easier, more consistent and more
          professional.
        </P>
      </Section>
    </LegalLayout>
  );
}
